import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import SelectorColor from '../../components/SelectorColor'

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
const EMOJIS = ['🏢', '🛍️', '🍔', '🏠', '💊', '🎓', '🚗', '✂️', '🏥', '⚽', '💻', '📸', '🌮', '👗', '🐶', '🔧']
// La paleta y el panel de "más colores" viven en SelectorColor

const API_VACIA = () => ({ id: uid(), nombre: '', descripcion: '', urlBase: '', authTipo: 'none', authHeader: '', authValor: '', activo: true, endpoints: [] })
const EP_VACIO  = () => ({ id: uid(), path: '', metodo: 'GET', descripcion: '', parametros: [] })

// ─── Campo password con toggle ────────────────────────────────────────────────
function SecretInput({ value, onChange, placeholder, isSet }) {
  const [v, setV] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input className="input" type={v ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)}
        placeholder={isSet ? '••••••••  (vacío = no cambiar)' : placeholder} style={{ paddingRight: 40 }} autoComplete="off" />
      <button type="button" onClick={() => setV(!v)}
        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}>
        {v ? '🙈' : '👁'}
      </button>
    </div>
  )
}

function NegocioStats({ negocioId, onVerInbox }) {
  const [stats, setStats] = useState(null)
  useEffect(() => {
    api.get(`/negocios/${negocioId}/stats`).then(r => setStats(r.data)).catch(() => {})
  }, [negocioId])
  if (!stats) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 className="card-title" style={{ margin: 0 }}>📊 Estadísticas del negocio</h3>
        <button className="btn btn-sm btn-primary" onClick={onVerInbox}>💬 Ver inbox completo</button>
      </div>
      <div className="negocio-inner-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Chats abiertos', valor: stats.chatsAbiertos, color: '#22c55e' },
          { label: 'Total de chats', valor: stats.chatsTotales, color: '#3b82f6' },
          { label: 'Mensajes este mes', valor: stats.mensajesMes, color: '#8b5cf6' },
        ].map(s => (
          <div key={s.label} style={{ background: s.color + '22', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.valor}</div>
            <div style={{ fontSize: 12, color: s.color, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {stats.sesiones?.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Números en este negocio</div>
          {stats.sesiones.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.status === 'conectado' ? '#22c55e' : '#94a3b8', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.telefono || 'Sin número'}</div>
              </div>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                background: s.status === 'conectado' ? '#22c55e22' : 'var(--bg)',
                color: s.status === 'conectado' ? '#22c55e' : 'var(--text-muted)' }}>
                {s.status}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default function Negocios() {
  const navigate = useNavigate()
  const [negocios, setNegocios] = useState([])
  const [numeros, setNumeros] = useState([])
  const [editando, setEditando] = useState(null)   // objeto negocio en edición
  const [tab, setTab] = useState('identidad')
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [creando, setCreando] = useState(null)          // { nombre, tipo } del negocio nuevo
  const [guardandoNuevo, setGuardandoNuevo] = useState(false)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [n, s] = await Promise.all([
        api.get('/negocios'),
        api.get('/negocios/numeros-disponibles'),
      ])
      setNegocios(n.data)
      setNumeros(s.data)
    } catch { toast.error('Error cargando negocios') }
    finally { setCargando(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  // Se pregunta el tipo al crear porque de ahí sale el tablero: un negocio de
  // productos y uno de servicios no venden igual y no pueden compartir etapas.
  const crear = () => setCreando({ nombre: '', tipo: 'servicios' })

  const confirmarCrear = async () => {
    if (!creando?.nombre.trim()) { toast.error('Ponle un nombre al negocio'); return }
    setGuardandoNuevo(true)
    try {
      const { data } = await api.post('/negocios', { nombre: creando.nombre.trim(), tipo: creando.tipo })
      toast.success(data.pipelineCreado ? `✅ Negocio creado con su tablero "${data.pipelineCreado}"` : '✅ Negocio creado')
      setCreando(null)
      await cargar()
      abrir(data)
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error')
    } finally { setGuardandoNuevo(false) }
  }

  const abrir = (negocio) => {
    setEditando(JSON.parse(JSON.stringify(negocio)))
    setTab('identidad')
  }

  const upd = (k, v) => setEditando(prev => ({ ...prev, [k]: v }))

  const guardar = async () => {
    setGuardando(true)
    try {
      // 1. Guardar datos del negocio
      await api.put(`/negocios/${editando.id}`, editando)
      // 2. Guardar números asignados
      const sesionIds = numeros.filter(n => n.unidadNegocioId === editando.id).map(n => n.id)
      await api.post(`/negocios/${editando.id}/numeros`, { sesionIds })
      toast.success('✅ Negocio guardado')
      await cargar()
      setEditando(null)
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar') }
    finally { setGuardando(false) }
  }

  const eliminar = async (negocio) => {
    if (!window.confirm(`¿Eliminar el negocio "${negocio.nombre}"? Sus números quedarán sin asignar.`)) return
    try {
      await api.delete(`/negocios/${negocio.id}`)
      toast.success('Negocio eliminado')
      setEditando(null)
      await cargar()
    } catch (e) { toast.error(e.response?.data?.error || 'Error') }
  }

  // Toggle de asignación de un número (en estado local de `numeros`)
  const toggleNumero = (sesionId) => {
    setNumeros(prev => prev.map(n =>
      n.id === sesionId
        ? { ...n, unidadNegocioId: n.unidadNegocioId === editando.id ? null : editando.id }
        : n))
  }

  // ── Helpers APIs externas (sobre editando.apisExternas) ──
  const apis = editando?.apisExternas || []
  const setApis = (arr) => upd('apisExternas', arr)
  const addApi = () => setApis([...apis, API_VACIA()])
  const updApi = (id, k, v) => setApis(apis.map(a => a.id === id ? { ...a, [k]: v } : a))
  const delApi = (id) => setApis(apis.filter(a => a.id !== id))
  const addEp = (apiId) => updApi(apiId, 'endpoints', [...(apis.find(a => a.id === apiId).endpoints || []), EP_VACIO()])
  const updEp = (apiId, epId, k, v) => updApi(apiId, 'endpoints', apis.find(a => a.id === apiId).endpoints.map(e => e.id === epId ? { ...e, [k]: v } : e))
  const delEp = (apiId, epId) => updApi(apiId, 'endpoints', apis.find(a => a.id === apiId).endpoints.filter(e => e.id !== epId))

  const probarApi = async (apiObj, ep) => {
    try {
      const { data } = await api.post(`/negocios/${editando.id}/test-api`, {
        api: apiObj, endpoint: { path: ep.path, metodo: ep.metodo }, parametros: {},
      })
      data.ok ? toast.success(`✅ Conexión OK (${data.status})`) : toast.error(`❌ ${data.error || data.status}`)
    } catch (e) { toast.error(e.response?.data?.error || 'Error de conexión') }
  }

  if (cargando) return <div className="loading-fullscreen"><div className="spinner" /></div>

  // ════════════════ VISTA EDICIÓN ════════════════
  if (editando) {
    const misNumeros = numeros.filter(n => n.unidadNegocioId === editando.id)
    const TABS = [
      { id: 'identidad', icon: '🏷️', label: 'Identidad' },
      { id: 'contexto',  icon: '📖', label: 'Contexto' },
      { id: 'numeros',   icon: '📱', label: `Números (${misNumeros.length})` },
      { id: 'stats',     icon: '📊', label: 'Estadísticas' },
      { id: 'ia',        icon: '🤖', label: 'IA' },
      { id: 'apis',      icon: '🔌', label: `APIs (${apis.length})` },
    ]
    return (
      <div className="page" style={{ maxWidth: 860 }}>
        <button className="btn btn-ghost" style={{ marginBottom: 14 }} onClick={() => setEditando(null)}>← Volver a negocios</button>

        <div className="negocio-edit-header" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: editando.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, flexShrink: 0 }}>{editando.emoji}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{editando.nombre || 'Negocio'}</h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{misNumeros.length} número(s) · {apis.length} API(s)</div>
          </div>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>{guardando ? '⏳...' : '💾 Guardar'}</button>
          <button className="btn" style={{ color: '#ef4444', borderColor: '#fca5a5' }} onClick={() => eliminar(editando)}>🗑</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '8px 16px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', fontSize: 13,
                fontWeight: tab === t.id ? 700 : 500,
                background: tab === t.id ? 'var(--primary)' : 'transparent',
                color: tab === t.id ? 'white' : 'var(--text-soft)' }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* IDENTIDAD */}
        {tab === 'identidad' && (
          <div className="card">
            <div className="form-group">
              <label className="form-label">Nombre del negocio</label>
              <input className="input" value={editando.nombre} onChange={e => upd('nombre', e.target.value)} placeholder="Ej: Zapatería Centro" />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción corta</label>
              <input className="input" value={editando.descripcion || ''} onChange={e => upd('descripcion', e.target.value)} placeholder="Ej: Venta de calzado deportivo y casual" />
            </div>
            <div className="form-group">
              <label className="form-label">¿Qué vende?</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[['productos', '🛍️ Productos'], ['servicios', '🏢 Servicios'], ['ambos', '🔀 Las dos cosas']].map(([id, etiqueta]) => (
                  <button key={id} type="button" onClick={() => upd('tipo', id)}
                    style={{
                      padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      border: `2px solid ${(editando.tipo || 'servicios') === id ? 'var(--primary)' : 'var(--border)'}`,
                      background: (editando.tipo || 'servicios') === id ? 'var(--bg-active)' : 'var(--bg)',
                      color: 'var(--text)',
                    }}>{etiqueta}</button>
                ))}
              </div>
              <div className="form-hint" style={{ marginTop: 6 }}>
                Define las etapas con las que nace su tablero y dónde se registra lo que cierra:
                un negocio de productos termina en un pedido, uno de servicios en una cotización.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Emoji</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => upd('emoji', e)} style={{ fontSize: 22, width: 42, height: 42, borderRadius: 10, cursor: 'pointer',
                    border: `2px solid ${editando.emoji === e ? 'var(--primary)' : 'var(--border)'}`, background: editando.emoji === e ? 'var(--bg-active)' : 'var(--bg)' }}>{e}</button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <SelectorColor value={editando.color} onChange={c => upd('color', c)} />
            </div>
            <div className="flex justify-between items-center" style={{ paddingTop: 10 }}>
              <div><div style={{ fontWeight: 600 }}>Negocio activo</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Si está inactivo, su contexto no se aplica al bot</div></div>
              <div className={`toggle ${editando.activo ? 'on' : ''}`} onClick={() => upd('activo', !editando.activo)} />
            </div>
          </div>
        )}

        {/* CONTEXTO */}
        {tab === 'contexto' && (
          <div className="card">
            <h3 className="card-title">📖 Información del negocio</h3>
            <p className="card-subtitle">Estos datos se inyectan al bot de TODOS los números de este negocio.</p>
            <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group"><label className="form-label">📍 Dirección</label><input className="input" value={editando.direccion || ''} onChange={e => upd('direccion', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">📞 Teléfono</label><input className="input" value={editando.telefono || ''} onChange={e => upd('telefono', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">✉️ Email</label><input className="input" value={editando.email || ''} onChange={e => upd('email', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">🌐 Sitio web</label><input className="input" value={editando.sitioWeb || ''} onChange={e => upd('sitioWeb', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">🕐 Horario</label><input className="input" value={editando.horario || ''} onChange={e => upd('horario', e.target.value)} placeholder="Lun-Vie 9am-7pm, Sáb 9am-2pm" /></div>
            <div className="form-group">
              <label className="form-label">🧠 Contexto e instrucciones para la IA</label>
              <textarea className="textarea" rows={8} value={editando.contextoIA || ''} onChange={e => upd('contextoIA', e.target.value)}
                placeholder={'Describe el negocio, productos/servicios, políticas, promociones, formas de pago, preguntas frecuentes, tono deseado, etc.\n\nEjemplo:\nSomos una zapatería con 3 sucursales. Vendemos calzado deportivo Nike, Adidas y Puma.\nEnvíos gratis arriba de $1,500. Aceptamos tarjeta, transferencia y efectivo.\nPolítica de cambios: 30 días con ticket.'} />
              <div className="form-hint">Cuanto más completo, mejor responderá el bot de este negocio.</div>
            </div>
          </div>
        )}

        {/* NÚMEROS */}
        {tab === 'numeros' && (
          <div className="card">
            <h3 className="card-title">📱 Números de WhatsApp de este negocio</h3>
            <p className="card-subtitle">Marca los números que pertenecen a este negocio. Compartirán su contexto y APIs.</p>
            {numeros.length === 0 && <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>No hay números creados. Créalos en la sección "Números".</div>}
            {numeros.map(n => {
              const asignadoAqui = n.unidadNegocioId === editando.id
              const asignadoAOtro = n.unidadNegocioId && n.unidadNegocioId !== editando.id
              const otro = asignadoAOtro ? negocios.find(b => b.id === n.unidadNegocioId) : null
              return (
                <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 8, cursor: 'pointer',
                  border: `2px solid ${asignadoAqui ? 'var(--primary)' : 'var(--border)'}`, background: asignadoAqui ? 'var(--bg-active)' : 'var(--bg-card)' }}>
                  <input type="checkbox" checked={asignadoAqui} onChange={() => toggleNumero(n.id)} style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: n.color || 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700 }}>{(n.nombre || '?')[0].toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{n.nombre}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{n.telefono || 'Sin número'} · {n.status}</div>
                  </div>
                  {asignadoAOtro && <span style={{ fontSize: 11, color: '#d97706', background: '#d9770622', padding: '2px 8px', borderRadius: 20 }}>En "{otro?.nombre || 'otro'}"</span>}
                </label>
              )
            })}
          </div>
        )}

        {/* ESTADÍSTICAS */}
        {tab === 'stats' && <NegocioStats negocioId={editando.id} onVerInbox={() => navigate(`/inbox?negocio=${editando.id}&nombre=${encodeURIComponent(editando.nombre)}`)} />}

        {/* IA */}
        {tab === 'ia' && (
          <div className="card">
            <h3 className="card-title">🤖 API Key de Gemini propia del negocio</h3>
            <p className="card-subtitle">Opcional. Si la dejas vacía, se usa la key de la empresa o del sistema. Útil para separar costos/límites por negocio.</p>
            <div className="form-group">
              <label className="form-label">Google Gemini API Key</label>
              <SecretInput value={editando.geminiApiKey?.startsWith('••••') ? '' : (editando.geminiApiKey || '')}
                onChange={v => upd('geminiApiKey', v)} placeholder="AIzaSy..." isSet={editando.geminiApiKeySet} />
              <div className="form-hint">Obténla gratis en <strong>aistudio.google.com</strong></div>
            </div>
          </div>
        )}

        {/* APIS */}
        {tab === 'apis' && (
          <div>
            <div style={{ background: 'var(--primary)1a', background: 'color-mix(in srgb, var(--primary) 12%, var(--bg-card))', border: '1px solid var(--primary)', borderRadius: 12, padding: 16, marginBottom: 16, opacity: .9 }}>
              <div style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>🔌 APIs propias de este negocio</div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>Conecta el stock, precios o sistema de este negocio. El bot las consultará en tiempo real al responder. Cada negocio tiene sus propias APIs independientes.</div>
            </div>

            {apis.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, border: '2px dashed var(--border)', borderRadius: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🔌</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Sin APIs en este negocio</div>
                <button className="btn btn-primary" onClick={addApi}>+ Agregar API</button>
              </div>
            )}

            {apis.map(apiObj => (
              <div key={apiObj.id} className="card" style={{ marginBottom: 14 }}>
                <div className="flex justify-between items-center" style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700 }}>🔗 {apiObj.nombre || 'Nueva API'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className={`toggle ${apiObj.activo ? 'on' : ''}`} onClick={() => updApi(apiObj.id, 'activo', !apiObj.activo)} />
                    <button className="btn" style={{ color: '#ef4444', borderColor: '#fca5a5', padding: '4px 10px' }} onClick={() => delApi(apiObj.id)}>🗑</button>
                  </div>
                </div>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label className="form-label">Nombre</label><input className="input" value={apiObj.nombre} onChange={e => updApi(apiObj.id, 'nombre', e.target.value)} placeholder="Inventario" /></div>
                  <div className="form-group"><label className="form-label">URL base</label><input className="input" value={apiObj.urlBase} onChange={e => updApi(apiObj.id, 'urlBase', e.target.value)} placeholder="https://api.misistema.com" /></div>
                </div>
                <div className="form-group"><label className="form-label">¿Qué hace? (para que la IA sepa cuándo usarla)</label><input className="input" value={apiObj.descripcion} onChange={e => updApi(apiObj.id, 'descripcion', e.target.value)} placeholder="Consulta stock y precios de productos" /></div>
                <div className="api-auth-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Autenticación</label>
                    <select className="select" value={apiObj.authTipo} onChange={e => updApi(apiObj.id, 'authTipo', e.target.value)}>
                      <option value="none">Sin auth</option>
                      <option value="bearer">Bearer Token</option>
                      <option value="header">Header personalizado</option>
                      <option value="query">Query param</option>
                      <option value="basic">Basic Auth</option>
                    </select>
                  </div>
                  {(apiObj.authTipo === 'header' || apiObj.authTipo === 'query') && (
                    <div className="form-group"><label className="form-label">Nombre del header/param</label><input className="input" value={apiObj.authHeader} onChange={e => updApi(apiObj.id, 'authHeader', e.target.value)} placeholder="X-API-Key" /></div>
                  )}
                  {apiObj.authTipo !== 'none' && (
                    <div className="form-group"><label className="form-label">Valor / Key</label>
                      <SecretInput value={String(apiObj.authValor || '').startsWith('••••') ? '' : (apiObj.authValor || '')} onChange={v => updApi(apiObj.id, 'authValor', v)} placeholder="tu-api-key" isSet={apiObj.authValorSet} />
                    </div>
                  )}
                </div>

                {/* Endpoints */}
                <div style={{ marginTop: 8 }}>
                  <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Endpoints ({(apiObj.endpoints || []).length})</span>
                    <button className="btn btn-sm" onClick={() => addEp(apiObj.id)}>+ Endpoint</button>
                  </div>
                  {(apiObj.endpoints || []).map(ep => (
                    <div key={ep.id} style={{ background: 'var(--bg)', borderRadius: 10, padding: 12, marginBottom: 8 }}>
                      <div className="ep-grid" style={{ display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: 8, alignItems: 'end' }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Método</label>
                          <select className="select" value={ep.metodo} onChange={e => updEp(apiObj.id, ep.id, 'metodo', e.target.value)}><option>GET</option><option>POST</option></select>
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Path</label>
                          <input className="input" value={ep.path} onChange={e => updEp(apiObj.id, ep.id, 'path', e.target.value)} placeholder="/productos/{sku}" />
                        </div>
                        <button className="btn btn-sm" style={{ color: '#ef4444' }} onClick={() => delEp(apiObj.id, ep.id)}>✕</button>
                      </div>
                      <div className="form-group" style={{ margin: '8px 0 0' }}>
                        <input className="input" value={ep.descripcion} onChange={e => updEp(apiObj.id, ep.id, 'descripcion', e.target.value)} placeholder="Descripción: consultar producto por SKU" />
                      </div>
                      <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => probarApi(apiObj, ep)} disabled={!apiObj.urlBase || !ep.path}>▶ Probar conexión</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {apis.length > 0 && <button className="btn" onClick={addApi}>+ Agregar otra API</button>}
          </div>
        )}
      </div>
    )
  }

  // ════════════════ VISTA LISTA ════════════════
  const totalChatsAbiertos = negocios.reduce((a, n) => a + (n.chatsAbiertos || 0), 0)
  const totalNumeros = negocios.reduce((a, n) => a + (n.Sesions || []).length, 0)

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>🏢 Negocios</h1>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>Administra negocios con sus números, contexto de IA y APIs independientes.</p>
        </div>
        <button className="btn btn-primary" onClick={crear}>+ Nuevo negocio</button>
      </div>

      {/* Stats globales */}
      {negocios.length > 0 && (
        <div className="negocio-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Negocios', valor: negocios.length, icon: '🏢', color: '#6366f1' },
            { label: 'Números activos', valor: totalNumeros, icon: '📱', color: '#25D366' },
            { label: 'Chats abiertos', valor: totalChatsAbiertos, icon: '💬', color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.valor}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {negocios.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', border: '2px dashed var(--border)', borderRadius: 16 }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>🏢</div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Aún no tienes negocios</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Crea tu primer negocio para agrupar números con su propio contexto y API</div>
          <button className="btn btn-primary" onClick={crear}>+ Crear primer negocio</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {negocios.map(n => (
            <div key={n.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', opacity: n.activo ? 1 : 0.65, boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
              {/* Barra de color top */}
              <div style={{ height: 5, background: n.color }} />

              <div style={{ padding: '16px 16px 12px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: n.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>{n.emoji}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{n.nombre}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.descripcion || 'Sin descripción'}</div>
                  </div>
                  {!n.activo && <span style={{ fontSize: 10, background: 'var(--bg)', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: 20, flexShrink: 0 }}>Inactivo</span>}
                </div>

                {/* Stats del negocio */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <div style={{ background: '#22c55e22', borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>{n.chatsAbiertos || 0}</div>
                    <div style={{ fontSize: 11, color: '#22c55e' }}>Chats abiertos</div>
                  </div>
                  <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{(n.Sesions || []).length}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Números</div>
                  </div>
                </div>

                {/* Números con status dots */}
                {(n.Sesions || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                    {n.Sesions.slice(0, 5).map(s => (
                      <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'var(--bg)', padding: '3px 8px', borderRadius: 20, border: '1px solid var(--border)' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.status === 'conectado' ? '#22c55e' : '#94a3b8', flexShrink: 0 }} />
                        {s.nombre}
                        {s.chatsAbiertos > 0 && <span style={{ background: '#22c55e', color: 'white', fontSize: 9, fontWeight: 800, padding: '0 4px', borderRadius: 10 }}>{s.chatsAbiertos}</span>}
                      </span>
                    ))}
                    {n.Sesions.length > 5 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{n.Sesions.length - 5}</span>}
                  </div>
                )}

                {/* Botones */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm" style={{ flex: 1, background: 'var(--primary)22', border: '1px solid var(--primary)', color: 'var(--primary)', fontWeight: 600 }}
                    onClick={e => { e.stopPropagation(); navigate(`/inbox?negocio=${n.id}&nombre=${encodeURIComponent(n.nombre)}`) }}>
                    💬 Ver inbox
                  </button>
                  <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => abrir(n)}>
                    ✏️ Editar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creando && (
        <div className="modal-overlay" onClick={() => setCreando(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Nuevo negocio</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setCreando(null)}>✕</button>
            </div>
            <div className="modal-body">
              <label className="form-label">Nombre</label>
              <input
                className="input" autoFocus placeholder="Ej: Mikus, Alphoran…"
                value={creando.nombre}
                onChange={e => setCreando(c => ({ ...c, nombre: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') confirmarCrear() }}
                style={{ marginBottom: 18 }}
              />

              <label className="form-label">¿Qué vende este negocio?</label>
              <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                {[
                  { id: 'productos', emoji: '🛍️', titulo: 'Productos',
                    desc: 'Tiene inventario y precios de lista. Lo que cierra es un pedido, con su envío.',
                    tablero: 'Consulta → Interesado → Pedido armado → Vendido' },
                  { id: 'servicios', emoji: '🏢', titulo: 'Servicios',
                    desc: 'Cotiza un trabajo a medida. Lo que cierra es una cotización que luego se entrega por fases.',
                    tablero: 'Lead nuevo → Calificado → Cotizado → En negociación → Ganado' },
                  { id: 'ambos', emoji: '🔀', titulo: 'Las dos cosas',
                    desc: 'Vende productos y además presta servicios.',
                    tablero: 'Usa las etapas de servicios, que contienen a las de productos' },
                ].map(o => {
                  const sel = creando.tipo === o.id
                  return (
                    <button
                      key={o.id} type="button"
                      onClick={() => setCreando(c => ({ ...c, tipo: o.id }))}
                      style={{
                        display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left',
                        padding: 12, borderRadius: 'var(--radius)', cursor: 'pointer',
                        background: sel ? 'var(--bg-hover)' : 'transparent',
                        border: `2px solid ${sel ? 'var(--primary)' : 'var(--border)'}`,
                      }}
                    >
                      <span style={{ fontSize: 22 }}>{o.emoji}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{o.titulo}</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{o.desc}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 5, fontFamily: 'monospace' }}>{o.tablero}</span>
                      </span>
                    </button>
                  )
                })}
              </div>

              <div className="form-hint" style={{ marginTop: 14 }}>
                Se le creará su propio tablero de Pipeline con esas etapas, separado del de los demás negocios.
                Puedes cambiar el tipo y las etapas después.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setCreando(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarCrear} disabled={guardandoNuevo}>
                {guardandoNuevo ? 'Creando…' : 'Crear negocio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
